// src/modules/bookLocation/keyHandover/pages/newSubscription/SubscriptionHostDataPage.tsx

import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import type { RootState } from "../../../../../redux/store";
import { KeyHandoverPageLayout } from "../../components/KeyHandoverPageLayout";
import { KeyHandoverContentWrapper } from "../../components/KeyHandoverContentWrapper";
import { Spinner } from "../../../../../components/ui/spinner";
import routes from "../../../../../routes/routes";
import { useLocationHydration } from "../../hooks/useLocationHydration";
import { useSubscriptionHydration } from "../../hooks/useSubscriptionHydration";
import { useSubscriptionUrlSync } from "../../hooks/useSubscriptionUrlSync";
import KeyHandoverNavigateNextAction from "../../common/util/KeyHandoverNavigateNextAction";
import SubscriptionHostDataStep from "../../steps/KeyHandoverSubscriptionHostDataStep";

/**
 * Host Data Content
 * Handles hydration and displays host data form
 */
function HostDataContent() {
    const { t } = useTranslation();
    const hydratedRef = React.useRef(false);
    const [bothHydrated, setBothHydrated] = React.useState(false);

    const locationHydrated = useLocationHydration();
    const subscriptionHydrated = useSubscriptionHydration();

    // Mark as hydrated after both hydrations complete
    React.useEffect(() => {
        if (locationHydrated && subscriptionHydrated && !bothHydrated) {
            setBothHydrated(true);
            hydratedRef.current = true;
        }
    }, [locationHydrated, subscriptionHydrated, bothHydrated]);

    // Sync subscription state back to URL
    useSubscriptionUrlSync(hydratedRef);

    if (!bothHydrated) {
        return (
            <div className="flex justify-center items-center py-12">
                <Spinner />
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-4">
            <p
                className="-mt-5 text-xs text-muted-foreground text-center"
                role="status"
                aria-live="polite"
            >
                {t("book_location.keys.step_counter", {
                    current: 2,
                    total: 3,
                    defaultValue: "Schritt {{current}} von {{total}}",
                })}
            </p>
            <SubscriptionHostDataStep />
        </div>
    );
}

/**
 * Subscription Host Data Page
 *
 * Flow 2: New Subscription - Step 2
 * Collect company/host information for subscription
 */
const SubscriptionHostDataPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    const hostData = useSelector((state: RootState) => state.subscription.newSubscription.hostData);

    // Validation: check if required host data fields are filled
    const isValid = Boolean(
        hostData?.firstName && hostData?.lastName && hostData?.email && hostData?.phone
    );
    
    const handleNext = () => {
        navigate({
            pathname: routes.keyHandoverSubscriptionSummaryPath,
            search: location.search,
        });
    };

    const bottomActions = (
        <KeyHandoverNavigateNextAction
            onNext={handleNext}
            enabled={isValid}
            bounceKey={`host-${hostData?.firstName}-${hostData?.lastName}-${hostData?.email}-${hostData?.phone}`}
        />
    );

    return (
        <KeyHandoverPageLayout
            headerText={t("book_location.keys.subscription_host_data.header_text", {
                defaultValue: "Host",
            })}
            backTo={routes.keyHandoverSubscriptionPlanSelectionPath}
            bottomActions={bottomActions}
        >
            <KeyHandoverContentWrapper>
                <HostDataContent />
            </KeyHandoverContentWrapper>
        </KeyHandoverPageLayout>
    );
};

export default SubscriptionHostDataPage;
